import { useState } from 'react';
import {
  Badge,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  Menu,
  MenuItem,
  Select,
  Avatar,
} from '@mui/material';
import LanguageSelector from 'Components/LanguageSelector';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import Logout from '@mui/icons-material/Logout';
import { Main, UserContainerMenu } from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { logoff, updateTenant, useAuthTenant, useAuthUser } from 'store/auth';
import { useTranslation } from 'react-i18next';
import { getSelectedObject } from 'utils/select_utils';
import { getTenant } from 'utils/tenant_utils';
import { changeStatusMenu } from 'store/menu';
import { useQuery } from 'react-query';
import { getTotalCountUserNotification, getUserNotifications } from 'services/user-notifications-service';
import { MenuNotifications } from './components/MenuNotifications/MenuNotifications';

export const Header = ({ openMenu }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation('common');
  const [anchorEl, setAnchorEl] = useState(null);
  const [showMenuNotification, setShowMenuNotification] = useState(false);
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleCloseLogoff = () => {
    setAnchorEl(null);
    onLogoff();
  };
  const onLogoff = () => {
    dispatch(logoff());
  };
  const user = useSelector(useAuthUser);
  const selectedTenant = useSelector(useAuthTenant);

  // Ordena empresas: Virtual Office sempre primeiro, depois a selecionada, depois as demais por nome
  const selectedCompany = getSelectedObject(user.companiesWithAccess, selectedTenant);
  const orderedCompanies = [...(user.companiesWithAccess ?? [])].sort((a, b) => {
    // Virtual Office sempre no topo
    if (a?.name === 'Virtual Officer') return -1;
    if (b?.name === 'Virtual Officer') return 1;
    
    // Empresa selecionada vem depois do Virtual Office
    if (selectedCompany && a?._id?.toString() === selectedCompany._id?.toString()) return -1;
    if (selectedCompany && b?._id?.toString() === selectedCompany._id?.toString()) return 1;
    
    // Demais empresas ordenadas alfabeticamente
    const localeMap = { pt: 'pt-BR', en: 'en', es: 'es' };
    return (a?.name ?? '').localeCompare(b?.name ?? '', localeMap[i18n.language] || 'en', { sensitivity: 'base' });
  });

  const { data: totalUserNotifications } = useQuery(['getTotalCountUserNotification', user.userId, selectedTenant?.slug], async () => {
    if (selectedTenant?.slug === undefined || selectedTenant?.slug === null) return 0;
    const result = await getTotalCountUserNotification(user.userId, selectedTenant.slug);
    return result?.data?.total || 0;
  });

  const { data: notifications } = useQuery(['getNotifications', user.userId, selectedTenant?.slug, totalUserNotifications], async () => {
    if (selectedTenant?.slug === undefined || selectedTenant?.slug === null) return [];
    if (!totalUserNotifications || totalUserNotifications === 0) return [];

    const result = await getUserNotifications(user.userId, selectedTenant.slug, 1, 5, '');
    return result?.data?.data || [];
  }, { enabled: totalUserNotifications > 0 });
  return (
    <Main openMenu={openMenu}>
      <div id="wrapper">
        <div
          className="menu-mobile"
          onClick={() => {
            dispatch(changeStatusMenu());
          }}
        >
          <MenuOutlinedIcon id="menu" style={{ fill: '#0039C5', height: '32px', width: '32px' }} />
          <p>Menu</p>
        </div>
        
        {/* Chip centralizado e destacado */}
        {selectedTenant?.slug !== getTenant() && (
          <div className="company-chip-container">
            <Chip 
              avatar={
                selectedTenant?.logo ? (
                  <Avatar 
                    src={selectedTenant.logo} 
                    alt={selectedTenant.name}
                    sx={{ width: 28, height: 28 }}
                  />
                ) : undefined
              }
              label={t('header.accessingCompany', { name: selectedTenant?.name })}
              variant="filled" 
              color="primary"
              sx={{
                backgroundColor: 'rgb(0, 57, 197)',
                color: '#ffffff',
                fontWeight: 'bold',
                fontSize: '14px',
                padding: '8px 16px',
                height: '40px',
                boxShadow: '0 4px 12px rgba(0, 57, 197, 0.3)',
                border: '2px solid #1976d2',
                '&:hover': {
                  backgroundColor: '#1976d2',
                  boxShadow: '0 6px 16px rgba(0, 57, 197, 0.4)',
                }
              }}
            />
          </div>
        )}
        
        <div className="user-section">
          <LanguageSelector />
          <UserContainerMenu
            onClick={handleClick}
            aria-controls={anchorEl ? 'user-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={anchorEl ? 'true' : undefined}
          >
            <AccountCircleIcon
              id="photo"
              style={{ fill: '#878787', height: '32px', width: '32px', marginRight: '8px' }}
            />
            <p>{user?.name ?? t('header.defaultUser')}</p>
            <ExpandMoreOutlinedIcon
              id="icon"
              style={{
                fill: '#0039C5',
                height: '28px',
                width: '28px',
                marginLeft: '8px',
                marginRight: '0px',
              }}
            />
          </UserContainerMenu>

          <IconButton
            onClick={() => {
              setShowMenuNotification(!showMenuNotification);
            }}
            sx={{
              borderRadius: '14px',
              '& > .MuiBadge-root': {
                width: '25px',
              },
              '.MuiButtonBase-root.MuiIconButton-root': {
                width: '30px',
                height: '30px',
              },
            }}
            size="large"
            aria-label={t('header.notificationLabel', { count: totalUserNotifications || 0 })}
          >
            <Badge
              badgeContent={totalUserNotifications ? `${totalUserNotifications}` : ''}
              color="error"
              overlap="circular"
              sx={{
                '.MuiBadge-badge': {
                  width: '20px',
                  minWidth: '20px',
                  top: '3%',
                  display: totalUserNotifications ? 'block' : 'none',
                },
              }}
            >
              <NotificationsNoneIcon id="notification" style={{ fill: '#FFFFFF', height: '24px', width: '24px' }} />
            </Badge>
          </IconButton>
        </div>
      </div>
      <Menu
        anchorEl={anchorEl}
        id="user-menu"
        open={anchorEl != null}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            minWidth: 210,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        // PaperProps={}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {user.companiesWithAccess.length > 0 && (
          <MenuItem
            onClick={handleClose}
            sx={{
              '&:hover': {
                backgroundColor: 'white!important',
              },
              '&.Mui-focusVisible': {
                backgroundColor: 'white!important',
              },
            }}
          >
            <FormControl fullWidth>
              <InputLabel id="tenant-label" shrink={true}>
                {t('header.company')}
              </InputLabel>
              <Select
                labelId="tenant-label"
                id="tenant"
                value={selectedCompany}
                label={t('header.company')}
                onChange={ev => {
                  dispatch(updateTenant({ tenant: ev.target.value }));
                }}
                notched
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 240,
                      overflowY: 'auto',
                    },
                  },
                }}
              >
                {orderedCompanies?.map((item, index) => {
                  const isSelected = selectedCompany?._id?.toString() === item?._id?.toString();
                  return (
                    <MenuItem
                      value={item}
                      key={index}
                      selected={isSelected}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(0, 57, 197, 0.12)!important',
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: 'rgba(0, 57, 197, 0.18)!important',
                        },
                      }}
                    >
                      {item.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </MenuItem>
        )}
        {user.companiesWithAccess.length > 0 && <Divider />}
        <MenuItem onClick={handleCloseLogoff}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          {t('header.logout')}
        </MenuItem>
      </Menu>
      <MenuNotifications
        notifications={notifications || []}
        showMenu={showMenuNotification}
        totalNotifications={totalUserNotifications || 0}
      />
    </Main>
  );
};
